import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { FormAwareCoverSlide } from '@/modules/slideEditor/formAware/FormAwareCoverSlide';

import {
  SlideEditorBatchFormShape,
  usePresentationTemplateSlideEditorContext,
} from './PresentationTemplateSlideEditor.context';

export interface PresentationTemplateCoverSlideProps {
  bundleId: string;
}

/**
 * Template-editor binding for a cover-slide bundle: resolves the batch-form
 * field paths from the bundle ID and delegates form handling to the shared
 * {@link FormAwareCoverSlide}.
 */
export function PresentationTemplateCoverSlide({
  bundleId,
}: PresentationTemplateCoverSlideProps) {
  const { control } = useFormContext<SlideEditorBatchFormShape>();
  const { getBundleIndexById } = usePresentationTemplateSlideEditorContext();

  // Look up the stable index for this bundle ID to access form fields
  const bundleIndex = useMemo(
    () => getBundleIndexById(bundleId),
    [getBundleIndexById, bundleId]
  );

  const coverPage = useWatch({
    control,
    name: `bundles.${bundleIndex}.coverPage`,
  });

  // Early return if coverPage data isn't available - shouldn't happen for cover slide bundles
  if (bundleIndex === -1 || !coverPage) {
    return null;
  }

  return (
    <FormAwareCoverSlide<SlideEditorBatchFormShape>
      page={coverPage.page}
      bundle={coverPage.bundle}
      slideId="slide-editor"
      customContentPath={`bundles.${bundleIndex}.coverPage.page.customContent`}
      displayTitlePath={`bundles.${bundleIndex}.coverPage.page.displayTitle`}
      customPromptPath={`bundles.${bundleIndex}.coverPage.page.customPrompt`}
      highlightVariables
    />
  );
}
